import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { motion } from 'framer-motion';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { IoEyeOutline } from 'react-icons/io5';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';

function CompOffReports() {
  const [compOffs, setCompOffs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [requestsPerPage] = useState(8);
  const [apiLoading, setApiLoading] = useState(false);
  const { currentUser, loading } = useUser();
  const baseUrl = process.env.REACT_APP_API_URL;
  const [searchParams] = useSearchParams();
  const [selectedReport, setSelectedReport] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [status, setStatus] = useState("ALL");

  const fetchCompOffReports = async (page = 1, size = requestsPerPage) => {
    setApiLoading(true);
    try {
      const searchTerm = searchParams.get("search") || "";

      const response = await api.get(`/api/compoffs/comp-off-reports`, {
        params: {
          page: page - 1,
          size,
          search: searchTerm,
          status: status === "ALL" ? null : status
        }
      });

      const data = response.data;

      const mappedData = data.content.map(req => ({
        id: req.id,
        employeeName: req.employeeName,
        onDate: req.onDate,
        comOffStatus: req.comOffStatus,
        duration: req.duration,
        timing: req.timing,
        reason: req.reason,
        approverComment: req.approverComment,
        isUsed: req.isUsed,
        expiresOn: req.expiresOn,
        createdAt: req.createdAt
      }));

      setCompOffs(mappedData);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage + 1);
    } catch (error) {
      console.error('Failed to fetch Comp Off reports:', error);
    } finally {
      setApiLoading(false);
    }
  };


  const formatTiming = (timing) => {
  switch (timing) {
    case "FULL_DAY":
      return "Full Day";
    case "FIRST_HALF":
      return "First Half";
    case "SECOND_HALF":
      return "Second Half";
    default:
      return timing;
  }
};


  useEffect(() => {
    fetchCompOffReports(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchCompOffReports(1);
  }, [searchParams, status]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleView = (report) => {
    setSelectedReport(report);
    setShowViewModal(true);
  };

  if (loading || !currentUser) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <motion.div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="text-xl xs:text-2xl font-bold">Comp Off Reports</h2>

        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="text-sm text-slate-600 mr-2">Status:</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            >
              <option value="ALL">All</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>
      </motion.div>

      <motion.div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-left font-semibold text-slate-600">Sl. No</th>
                <th className="px-6 py-4 text-left font-semibold text-slate-600">Employee</th>
                <th className="px-6 py-4 text-left font-semibold text-slate-600">On Date</th>
                <th className="px-6 py-4 text-left font-semibold text-slate-600">Timing</th>        
                <th className="px-6 py-4 text-center font-semibold text-slate-600">Status</th>
                <th className="px-6 py-4 text-center font-semibold text-slate-600">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-8">Loading...</td>
                </tr>
              ) : compOffs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-slate-500">No comp off records found.</td>
                </tr>
              ) : (
                compOffs.map((report, index) => (
                  <tr key={report.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-slate-600">{(currentPage - 1) * requestsPerPage + index + 1}</td>
                    <td className="px-6 py-4">{report.employeeName}</td>
                    <td className="px-6 py-4">{formatDate(report.onDate)}</td>
                    <td className="px-6 py-4">{formatTiming(report.timing)}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        report.comOffStatus === "APPROVED"
                          ? "bg-green-100 text-green-800"
                          : report.comOffStatus === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {report.comOffStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleView(report)} className="text-blue-600 hover:text-blue-800">
                        <IoEyeOutline className="text-xl" />
                      </motion.button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {compOffs.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="large"
                  showFirstButton
                  showLastButton
                   sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* View Modal */}
      {showViewModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setShowViewModal(false); }}>
          <motion.div className="bg-white p-6 rounded-2xl max-w-xl w-full mx-4 shadow-xl relative" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <button onClick={() => setShowViewModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition">✕</button>
            <h2 className="text-xl font-semibold mb-4 text-center">Comp Off Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Employee Name</p>
                <p className="font-medium text-slate-800">{selectedReport.employeeName}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <p className="font-medium text-slate-800">{selectedReport.comOffStatus}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">On Date</p>
                <p className="font-medium text-slate-800">{formatDate(selectedReport.onDate)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Timing</p>
                <p className="font-medium text-slate-800">{formatTiming(selectedReport.timing)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Duration</p>
                <p className="font-medium text-slate-800">{selectedReport.duration} day(s)</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Used</p>
                <p className="font-medium text-slate-800">{selectedReport.isUsed ? "Yes" : "No"}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-slate-500">Reason</p>
                <p className="font-medium text-slate-800 whitespace-pre-line">{selectedReport.reason}</p>
              </div>
              {selectedReport.expiresOn && (
                <div className="sm:col-span-2">
                  <p className="text-sm text-slate-500">Expires On</p>
                  <p className="font-medium text-slate-800">{formatDate(selectedReport.expiresOn)}</p>
                </div>
              )}
            </div>
            <div className="mt-6 text-center">
              <button onClick={() => setShowViewModal(false)} className="px-6 py-2 bg-slate-800 text-white rounded-full hover:bg-slate-700 transition">Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default CompOffReports;
